/**
 * Whether "Allow start any job" (`ALLOW_START_ANY_JOB`) is on. Only a strict boolean `true` counts: a missing
 * field, any other value, or a configuration still loading or failed (`null`) means off.
 * @param {Object} frontEndConfig `useFrontEndConfig()`'s value
 * @returns {boolean}
 */
export const isAllowStartAnyJob = (frontEndConfig) => frontEndConfig?.allowStartAnyJob === true

// External data: rows of the jobs view may come without a part id; the part reference stands in, and with
// neither the row is compared by its fabrication order.
const partOf = (row) => row.partId ?? row.partReferenceId ?? null

const sharesSequenceWith = (row, isJobMode) => (item) => {
  if (!isJobMode) {
    return item.fabricationOrderId === row.fabricationOrderId
  }
  const rowPartId = partOf(row)
  const itemPartId = partOf(item)
  if (rowPartId == null || itemPartId == null) {
    return item.fabricationOrderId === row.fabricationOrderId
  }
  return itemPartId === rowPartId
}

const isBlockedByPreviousJob = (row, loadedRows, isJobMode) => {
  const rows = loadedRows
    .filter(sharesSequenceWith(row, isJobMode))
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
  const index = rows.findIndex(item => item.id === row.id)
  if (index <= 0) return false
  return !rows[index - 1].finished
}

/**
 * State of the Finish button of a details table row (story 195, FinishAvailability).
 * - A finished row: disabled, "Job already finished".
 * - Otherwise, with "Allow start any job" off and the previous row by start date (same fabrication order in
 *   the FO view, same part in the jobs view) not finished: disabled, "Previous job not finished".
 * - Otherwise: enabled, no tooltip.
 * @param {Object} row the row of the button
 * @param {Object[]} loadedRows the rows loaded in the table
 * @param {boolean} isJobMode true in the jobs view, false in the FO view
 * @param {boolean} allowStartAnyJob see `isAllowStartAnyJob`
 * @returns {{ disabled: boolean, tooltipKey: string }} `tooltipKey` is a translation key, `''` for none
 */
export const finishAvailability = (row, loadedRows, isJobMode, allowStartAnyJob) => {
  if (row.finished) {
    return { disabled: true, tooltipKey: 'Job already finished' }
  }
  if (!allowStartAnyJob && isBlockedByPreviousJob(row, loadedRows, isJobMode)) {
    return { disabled: true, tooltipKey: 'Previous job not finished' }
  }
  return { disabled: false, tooltipKey: '' }
}
